/**
 * Phone number formatting utilities for displaying normalized phone numbers in user-friendly formats.
 * These utilities work with backend-normalized phone numbers (digits only, optional + prefix).
 */

/**
 * Formats a normalized phone number for display.
 * - US numbers (10 digits): (123)-456-7890
 * - International numbers: +XX XXXXXXXXXX (with spaces for readability)
 * - Invalid/empty: returns empty string
 * 
 * @param phone - Normalized phone number (digits only, optional + prefix)
 * @returns Formatted phone number string for display
 * 
 * @example
 * formatPhoneNumber('1234567890') // '(123)-456-7890'
 * formatPhoneNumber('+441234567890') // '+44 1234567890'
 * formatPhoneNumber('') // ''
 * formatPhoneNumber(null) // ''
 * formatPhoneNumber(undefined) // ''
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  // Handle null, undefined, or empty string
  if (!phone || phone.trim() === '') {
    return '';
  }

  const trimmed = phone.trim();

  // Check if it's an international number (starts with +)
  if (trimmed.startsWith('+')) {
    // Extract just the digits after the +
    const digitsAfterPlus = trimmed.slice(1).replace(/\D/g, '');
    
    if (digitsAfterPlus.length === 0) {
      // If no digits after +, return as-is
      return trimmed;
    }
    
    // Determine country code length (1-3 digits typically)
    // For simplicity, we'll use a common heuristic: 
    // - If starts with 1, country code is 1 digit (US/Canada)
    // - Otherwise, assume 2-digit country code for most countries
    let countryCodeLength = 2;
    if (digitsAfterPlus.startsWith('1')) {
      countryCodeLength = 1;
    }
    
    // Make sure we don't exceed the actual length
    if (countryCodeLength > digitsAfterPlus.length) {
      countryCodeLength = digitsAfterPlus.length;
    }
    
    const countryCode = digitsAfterPlus.slice(0, countryCodeLength);
    const number = digitsAfterPlus.slice(countryCodeLength);
    
    // Format as +XX XXXXXXXXXX for readability
    return `+${countryCode} ${number}`;
  }

  // US number formatting (10 digits)
  const digitsOnly = trimmed.replace(/\D/g, '');
  
  if (digitsOnly.length === 10) {
    // Format as (123)-456-7890
    return `(${digitsOnly.slice(0, 3)})-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }

  // For other lengths, return digits only (fallback)
  return digitsOnly || trimmed;
}

/**
 * Validates if a phone number is in a valid format for display formatting.
 * This is a client-side check to ensure we can format the number properly.
 * 
 * @param phone - Phone number to validate
 * @returns true if the phone number can be formatted
 */
export function isValidForFormatting(phone: string | null | undefined): boolean {
  if (!phone || phone.trim() === '') {
    return false;
  }

  const trimmed = phone.trim();

  // International format: + followed by 8-15 digits
  if (trimmed.startsWith('+')) {
    const digitsAfterPlus = trimmed.slice(1).replace(/\D/g, '');
    return digitsAfterPlus.length >= 8 && digitsAfterPlus.length <= 15;
  }

  // US format: exactly 10 digits
  const digitsOnly = trimmed.replace(/\D/g, '');
  return digitsOnly.length === 10;
}
